import { FC, ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Tip } from '@components';
import { useTrans } from '@libs/i18n';

import type { ActiveTemplateField } from '../../../api/types';
import { readCardSx } from '../../utils/tokens';

import { fieldHelpText } from './fieldHelp';
import type { FieldMode } from './fieldInput.types';

interface Props {
  field: ActiveTemplateField;
  mode: FieldMode;
  dirty?: boolean;
  error?: string;
  children: ReactNode;
}

/**
 * Field wrapper matching the Organization Profile mockups. Read mode renders a
 * subtle rounded card holding a muted label and the value. Edit mode drops the
 * card, shows a bold label with an "Unsaved" pill when dirty, and lets the
 * per-type input render its own bordered container.
 */
export const FieldShell: FC<Props> = observer(
  ({ field, mode, dirty, error, children }) => {
    const { t } = useTrans();
    const label = field.label || field.key;
    // Every field carries a hint about what's expected — the catalog's own
    // description when it has one, otherwise a per-type fallback.
    const help = fieldHelpText(field, t);

    if (mode === 'read') {
      return (
        <Box sx={{ ...readCardSx, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Typography variant="textXS_semiBold" color="text.primary">
              {label}
            </Typography>
            <Tip title={help} iconSize={14} />
          </Box>
          {children}
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
            <Typography variant="textSM_bold" color="text.primary">
              {label}
            </Typography>
            <Tip title={help} iconSize={14} />
          </Box>
          {dirty && (
            <Box
              component="span"
              sx={{
                typography: 'textXS_semiBold',
                color: 'blue.700',
                px: 0.9,
                py: '2px',
                borderRadius: 999,
                backgroundColor: 'blue.100',
              }}
            >
              {t('app.orgProfile.field.unsaved')}
            </Box>
          )}
        </Box>
        {children}
        {error && (
          <Typography variant="textXS_regular" color="error.main">
            {error}
          </Typography>
        )}
      </Box>
    );
  },
);
